import {
    Component,
    DestroyRef,
    EventEmitter,
    inject,
    Input,
    OnInit,
    Output,
    ViewChild,
    ViewEncapsulation
} from '@angular/core';
import { FormBuilder } from '@angular/forms';
import { DatePipe } from '@angular/common';
import {filter, map, take, tap} from 'rxjs/operators';
import * as RA from 'ramda-adjunct';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {AttributesFacadeService} from '@emm/management/services/attributes-facade.service';
import {MatOption} from '@angular/material/core';
import {Attribute} from '@emm/core/models/attribute.model';
import {NotificationService} from '@emm/shared/services/notification.service';
import {MatDialog} from '@angular/material/dialog';
import {AddOptionDialogComponent} from '@emm/shared/dialogs/add-option-dialog/add-option-dialog.component';
import {API_ERRORS} from '@emm/core/util/api-constants';

@Component({
    encapsulation: ViewEncapsulation.None,
    selector: 'emm-management-attributes-form',
    styleUrls: ['./management-attributes-form.component.scss'],
    templateUrl: './management-attributes-form.component.html',
    standalone: false
})
export class ManagementAttributesFormComponent implements OnInit {

    @ViewChild('allSelected') allSelected: MatOption;
    @Input() isSuperAdmin: boolean;
    @Input() isAdmin: boolean;
    @Output() showButtons = new EventEmitter<boolean>();
    @Output() disableButtons = new EventEmitter<boolean>();
    attributeDetail$ = this.attributesFacade.attributeDetail$;
    attributeTypes$ = this.attributesFacade.attributeTypes$;
    private readonly destroyRef: DestroyRef = inject(DestroyRef);
    form;
    itemTypes = [];
    model = {};
    pipe = new DatePipe('en-US');
    attributeDetails;
    newOptions?: [];

    model$ = this.attributeDetail$.pipe(
        filter(RA.isNotNil),
        tap((details) => {
            this.attributeDetails = details;
        }),
        map(({ defaultValue, description, locked, name, regionScope, required, scope, itemTypeScope, sortOrder,
                 jsonProperty, type, validationExpression, attributeGroup, options, localeSupport}) => (this.form =
            this.formBuilder.group({
            name: [name, {disabled: true, nonNullable: true}],
            jsonProperty: [jsonProperty, {disabled: true, nonNullable: true}],
            description: [description, {disabled: true}],
            scope: [scope, {disabled: true, nonNullable: true}],
            itemTypeScope: [itemTypeScope, {disabled: !this.isSuperAdmin, nonNullable: true}],
            regionScope: [regionScope, {disabled: true, nonNullable: true}],
            type: [type, {disabled: true, nonNullable: true}],
            required: [required, {disabled: true, nonNullable: true}],
            locked: [locked, {disabled: true, nonNullable: true}],
            sortOrder: [sortOrder, {disabled: true, nonNullable: true}],
            defaultValue: [defaultValue, {disabled: true}],
            validationExpression: [validationExpression, {disabled: true, nonNullable: true}],
            attributeGroup: [attributeGroup, {disabled: true, nonNullable: true}],
            options: [null, {disabled: false, nonNullable: true}],
            localeSupport: [localeSupport, {disabled: false, nonNullable: true}]
        }))),
        tap((model) => this.model = model),
        tap(() => {
            const selectOrMultiSelect = ['scope', 'regionScope', 'itemTypeScope', 'type', 'required', 'locked', 'options'];
            this.form.disable();
            selectOrMultiSelect.forEach(field => {
                this.form.controls[field].enable();
            });
            if (this.isSuperAdmin || this.isAdmin) {
                this.form.controls['options'].enable();
            }
        }),
    );
    attribute: Attribute;

    constructor(private attributesFacade: AttributesFacadeService,
                private formBuilder: FormBuilder,
                private notificationService: NotificationService,
                private dialog: MatDialog,) {}



    ngOnInit(): void {
        this.attributesFacade.getAllItemTypes().pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
            types => {
                types.forEach(type => this.itemTypes.push({tag: type.tag, value: type.name}));
            }
        );
        this.model$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
            if (this.form?.controls){
                this.showButtons.emit(true);
            }

            if (this.form.controls['itemTypeScope'].value.length === 0 ||
                this.form.controls['itemTypeScope'].value.length === this.itemTypes.length)
            {
                this.form.controls['itemTypeScope'].patchValue([...this.itemTypes.map(type => type.tag), '0']);
                this.disableButtons.next(true);
                this.form?.controls['itemTypeScope']?.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
                    (value) => {
                        this.disableButtons.next(false);
                    });
            }
        });
        this.attributeDetail$.pipe(take(2)).subscribe(
            (attribute: Attribute) => this.attribute = attribute);
    }

    toggleSelectAll() {
        if (this.allSelected.selected) {
            this.form.controls['itemTypeScope']
                .patchValue([...this.itemTypes.map(type => type.tag), '0']);
        } else {
            this.form.controls['itemTypeScope'].patchValue([]);
        }
    }

    toggleSelectOne() {
        if (this.allSelected.selected) {
            this.allSelected.deselect();
            return false;
        }
        if(this.form.controls['itemTypeScope'].value.length === this.itemTypes.length)
            this.allSelected.select();
    }

    cancel() {
        if (this.newOptions?.length > 0) {
            for (const option of this.newOptions) {
                this.attribute.options.pop();
            }
        }
        this.form.reset();
        this.model$.pipe(take(1)).subscribe(() => {
            if (this.form.controls['itemTypeScope'].value.length === 0 ||
                this.form.controls['itemTypeScope'].value.length === this.itemTypes.length)
            {
                this.form.controls['itemTypeScope'].patchValue([...this.itemTypes.map(type => type.tag), '0']);
            }
        });
        this.disableButtons.next(true);
    }

    submit() {
        const updatedAttribute = {...this.attribute} as Attribute;
        updatedAttribute.itemTypeScope = this.form.controls['itemTypeScope'].value;
        if (updatedAttribute.itemTypeScope.includes('0') || this.form.controls.itemTypeScope.value.includes('0')) {
            updatedAttribute.itemTypeScope = [];
        }
        if (this.newOptions.length > 0) {
            const tempOptions = [];
            this.attributeDetails.options.forEach(option => {
                tempOptions.push(option['label'].trim());
            });
            updatedAttribute.options = tempOptions;
        }
        if (this.form.controls['itemTypeScope'].value.length > 0) {
            this.attributesFacade.updateAttribute(updatedAttribute).pipe(takeUntilDestroyed(this.destroyRef))
                .subscribe({
                    next: () => {
                        this.notificationService.success('Attribute updated successfully');
                        this.disableButtons.next(true);
                    },
                    error: (error) => {
                        this.notificationService.error(error?.error?.error.message || API_ERRORS.GenericErrorMessage);
                    },
                });
        } else {
            this.notificationService.error('Please select at least one item type scope');
        }
    }

    addNewOptions() {
        const dialogRef = this.dialog.open(AddOptionDialogComponent, {
            width: '500px',
            data: {}
        });
        dialogRef.afterClosed().pipe(takeUntilDestroyed(this.destroyRef)).subscribe(result => {
            const newOptions = result.split(',');
            const optionsFormattedForDropdown = [];
            newOptions.forEach(option => {
                option.trim();
                optionsFormattedForDropdown.push({
                    label: option,
                    value: option,
                });
            });
            this.newOptions = newOptions;
            optionsFormattedForDropdown.forEach(newOption => {
                this.attributeDetails.options.push(newOption);
            });
            this.disableButtons.next(false);
        });
    }
}
